/**
 * Permission Mapping Utilities
 * Handles legacy role → permission conversion and preset management
 */

import { CompanyPermissionKey, PermissionPreset } from '@/types/permissions';

// Legacy role to actual database preset mapping
export const LEGACY_ROLE_TO_PRESET: Record<string, string> = {
  'owner': 'owner',
  'super_admin': 'manager',
  'admin': 'manager', 
  'manager': 'manager',
  'member': 'basic_member',
  'viewer': 'basic_member',
  // Database preset keys (these are the actual keys)
  'basic_member': 'basic_member',
  'recruiter': 'recruiter',
  'hr_specialist': 'hr_specialist',
  'job_manager': 'job_manager',
  'team_lead': 'team_lead',
  'developer': 'developer',
  'intern': 'intern',
};

// Database permission presets (actual database keys)
export const PERMISSION_PRESETS = {
  OWNER: 'owner',
  MANAGER: 'manager',
  HR_SPECIALIST: 'hr_specialist',
  JOB_MANAGER: 'job_manager',
  TEAM_LEAD: 'team_lead',
  RECRUITER: 'recruiter',
  DEVELOPER: 'developer',
  BASIC_MEMBER: 'basic_member',
  INTERN: 'intern'
} as const;

export type PermissionPresetKey = typeof PERMISSION_PRESETS[keyof typeof PERMISSION_PRESETS];

// Permission preset definitions (using database presets)
export const PRESET_DEFINITIONS: Record<string, PermissionPreset> = {
  [PERMISSION_PRESETS.OWNER]: {
    preset_key: PERMISSION_PRESETS.OWNER,
    preset_name: 'Owner',
    description: 'Complete control over the company and all its resources',
    permissions: [CompanyPermissionKey.OWNER],
    category: 'management',
    is_system_preset: true,
  },
  [PERMISSION_PRESETS.MANAGER]: {
    preset_key: PERMISSION_PRESETS.MANAGER,
    preset_name: 'Manager',
    description: 'Full job management, team oversight, and hiring decisions',
    permissions: [
      CompanyPermissionKey.VIEW_ALL_JOBS,
      CompanyPermissionKey.CREATE_JOBS,
      CompanyPermissionKey.EDIT_JOBS,
      CompanyPermissionKey.MANAGE_JOB_STATUS,
      CompanyPermissionKey.VIEW_APPLICATIONS,
      CompanyPermissionKey.MANAGE_APPLICATIONS,
      CompanyPermissionKey.VIEW_TEAM,
      CompanyPermissionKey.MANAGE_COMPANY_PROFILE,
    ],
    category: 'management',
    is_system_preset: true,
  },
  [PERMISSION_PRESETS.BASIC_MEMBER]: {
    preset_key: PERMISSION_PRESETS.BASIC_MEMBER,
    preset_name: 'Basic Member',
    description: 'Standard team member with read-only access to jobs, company profile, and team information',
    permissions: [
      CompanyPermissionKey.VIEW_ASSIGNED_JOBS,
      CompanyPermissionKey.VIEW_JOB_LISTINGS,
      CompanyPermissionKey.VIEW_COMPANY_PROFILE,
      CompanyPermissionKey.VIEW_TEAM,
    ],
    category: 'basic',
    is_system_preset: true,
  },
};

/**
 * Convert legacy role to permission preset
 */
export function legacyRoleToPreset(legacyRole: string): string {
  return LEGACY_ROLE_TO_PRESET[legacyRole] || PERMISSION_PRESETS.BASIC_MEMBER;
}

/**
 * Get display name for permission preset from database keys
 */
export function getPresetDisplayName(presetKey: string): string {
  // Direct mapping for database preset keys
  switch (presetKey) {
    case 'owner':
      return 'Owner';
    case 'manager':
      return 'Manager';
    case 'hr_specialist':
      return 'HR Specialist';
    case 'job_manager':
      return 'Job Manager';
    case 'team_lead':
      return 'Team Lead';
    case 'recruiter':
      return 'Recruiter';
    case 'developer':
      return 'Developer';
    case 'basic_member':
      return 'Basic Member';
    case 'intern':
      return 'Intern';
    // Legacy fallbacks
    case 'admin':
    case 'super_admin':
      return 'Manager';
    case 'member':
      return 'Basic Member';
    case 'viewer':
      return 'Basic Member';
    default:
      return 'Basic Member';
  }
}

/**
 * Get translated display name for permission preset from database keys
 * This function should be used in components with access to translation context
 */
export function getTranslatedPresetDisplayName(presetKey: string, t: (key: string) => string): string {
  // Direct mapping for database preset keys
  switch (presetKey) {
    case 'owner':
      return t('team_management.roles.owner');
    case 'manager':
      return t('team_management.roles.manager');
    case 'hr_specialist':
      return 'HR Specialist'; // Keep as is for now
    case 'job_manager':
      return 'Job Manager'; // Keep as is for now
    case 'team_lead':
      return 'Team Lead'; // Keep as is for now
    case 'recruiter':
      return 'Recruiter'; // Keep as is for now
    case 'developer':
      return 'Developer'; // Keep as is for now
    case 'basic_member':
      return t('team_management.roles.basic_member');
    case 'intern':
      return 'Intern'; // Keep as is for now
    // Legacy fallbacks
    case 'admin':
    case 'super_admin':
      return t('team_management.roles.manager');
    case 'member':
      return t('team_management.roles.basic_member');
    case 'viewer':
      return t('team_management.roles.basic_member');
    case 'custom':
      return t('team_management.roles.custom');
    default:
      return t('team_management.roles.basic_member');
  }
}

/**
 * Get all permissions for a preset
 */
export function getPresetPermissions(presetKey: string): string[] {
  const preset = PRESET_DEFINITIONS[presetKey];
  return preset?.permissions || [];
}

/**
 * Check if preset has specific permission
 */
export function presetHasPermission(presetKey: string, permissionKey: string): boolean {
  // Owner preset grants all permissions
  if (presetKey === PERMISSION_PRESETS.OWNER) {
    return true;
  }
  
  const permissions = getPresetPermissions(presetKey);
  return permissions.includes(permissionKey);
}

/**
 * Get preset category for sorting/grouping
 */
export function getPresetCategory(presetKey: string): 'management' | 'team' | 'basic' {
  const preset = PRESET_DEFINITIONS[presetKey];
  return preset?.category || 'basic';
}

/**
 * Sort presets by hierarchy (leadership > management > general)
 */
export function sortPresetsByHierarchy(presets: string[]): string[] {
  const hierarchyOrder = {
    [PERMISSION_PRESETS.OWNER]: 1,
    [PERMISSION_PRESETS.MANAGER]: 2,
    [PERMISSION_PRESETS.HR_SPECIALIST]: 3,
    [PERMISSION_PRESETS.JOB_MANAGER]: 4,
    [PERMISSION_PRESETS.TEAM_LEAD]: 5,
    [PERMISSION_PRESETS.RECRUITER]: 6,
    [PERMISSION_PRESETS.DEVELOPER]: 7,
    [PERMISSION_PRESETS.BASIC_MEMBER]: 8,
    [PERMISSION_PRESETS.INTERN]: 9,
  };
  
  return presets.sort((a, b) => {
    const orderA = hierarchyOrder[a] || 999;
    const orderB = hierarchyOrder[b] || 999;
    return orderA - orderB;
  });
}

/**
 * Get all available permission presets (excluding owner for regular selection)
 */
export function getAllPresets(excludeOwner: boolean = false): PermissionPreset[] {
  const presets = Object.values(PRESET_DEFINITIONS);
  if (excludeOwner) {
    return presets.filter(preset => preset.preset_key !== PERMISSION_PRESETS.OWNER);
  }
  return presets;
}

/**
 * Check if preset is owner preset
 */
export function isOwnerPreset(presetKey: string): boolean {
  return presetKey === PERMISSION_PRESETS.OWNER;
}

/**
 * Get filtered presets for dropdown selection (excludes owner)
 */
export function getSelectablePresets(): PermissionPreset[] {
  return getAllPresets(true);
}

/**
 * Validate if a preset exists
 */
export function isValidPreset(presetKey: string): boolean {
  return presetKey in PRESET_DEFINITIONS;
}